import {useStripe, useElements, PaymentElement} from '@stripe/react-stripe-js';
import {Alert, Checkbox, Col, Row} from 'antd';
import {RollbackOutlined} from '@ant-design/icons';
import './style.css';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {getAllChapters} from '../../../Store/Actions';
import useStripeSubmit from '../../../hooks/useStripeSubmit';
import {formatCurrencyByLocalChapter} from '../../../Util/commonUtility';
import useLocalization from '../../../Localization';

const CommonPaymentCheckoutForm = ({
  clientSecret,
  dispatchSuccess,
  goBackURL,
  price,
  DataId,
  resetPayment,
  successUrl,
  localChapterId,
  extra,
  skipNavigateFromHook = false,
  isProcessingFeeAdded = false,
  onProcessingFeeToggle,
  isCardPaymentMethod = true,
  selectedPaymentMethodType = 'card',
  onPaymentMethodChange,
  disablePaymentSubmit = false,
  isIntentRefreshing = false,
  showProcessingFeeNotice = true,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const lastFetchedIntentStateRef = useRef('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const {ApplicationString, currentLanguage} = useLocalization();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allChapters = useSelector((state) => state.chapters.allChapters);
  const eventLocalChapterData = allChapters?.data?.chapters?.find(
    (obj) => obj.id === localChapterId,
  );
  useEffect(() => {
    if (!allChapters) {
      dispatch(getAllChapters());
    }
  }, [allChapters]);

  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  const [paymentElementError, setPaymentElementError] = useState(null);

  useEffect(() => {
    if (skipNavigateFromHook) {
      return;
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  // Do not call fetchUpdates until the Payment Element has finished its first
  // paint — early updates leave the iframe at height 0 on tall sponsor pages.
  useEffect(() => {
    if (!elements || !isPaymentElementReady) return;
    if (isIntentRefreshing || disablePaymentSubmit) return;
    if (typeof elements.fetchUpdates !== 'function') return;

    const intentState = `${clientSecret || ''}:${isProcessingFeeAdded}`;
    if (intentState === lastFetchedIntentStateRef.current) return;
    lastFetchedIntentStateRef.current = intentState;

    elements.fetchUpdates().catch(() => {});
  }, [
    clientSecret,
    disablePaymentSubmit,
    elements,
    isIntentRefreshing,
    isPaymentElementReady,
    isProcessingFeeAdded,
  ]);
  const handleSubmit = useStripeSubmit(
    successUrl,
    successUrl,
    (id, intent, status) => dispatchSuccess(id, intent, status),
    DataId,
    () => {
      dispatch(resetPayment());
    },
    skipNavigateFromHook,
  );

  const returnButtonhandler = () => {
    navigate(goBackURL);
    dispatch(resetPayment());
  };

  const processingFeeAmount = useMemo(() => {
    const base = Number(price || 0);
    if (!base) return 0;
    const totalWithFee = Math.round(base * 1.029 * 100) / 100;
    return Math.round((totalWithFee - base) * 100) / 100;
  }, [price]);

  const formattedProcessingFee =
    processingFeeAmount > 0
      ? localChapterId
        ? formatCurrencyByLocalChapter(
            processingFeeAmount,
            eventLocalChapterData,
            2,
            2,
          )
        : formatCurrencyByLocalChapter(processingFeeAmount, undefined, 2, 2)
      : null;
  const normalizedSelectedPaymentMethodType = String(
    selectedPaymentMethodType || '',
  )
    .trim()
    .toLowerCase();
  const isApplePaySelected =
    normalizedSelectedPaymentMethodType === 'apple_pay' ||
    normalizedSelectedPaymentMethodType === 'applepay';
  const processingFeeBeforeAmountText =
    ApplicationString[
      isApplePaySelected
        ? 'userAttendeeCartComponent.applePay.coverProcessingFeeBeforeAmount'
        : 'userAttendeeCartComponent.credit.coverProcessingFeeBeforeAmount'
    ];
  const processingFeeBeforeAmountParts =
    processingFeeBeforeAmountText.split('(2.9%)');

  return (
    <Row gutter={[0, 28]}>
      {goBackURL && (
        <Col span={24}>
          <div className='btn-main-container'>
            <button onClick={returnButtonhandler}>
              {' '}
              <span className='btn-icon-box'>
                <RollbackOutlined />
              </span>
              {
                ApplicationString[
                  'specialPaymentFormComponent.returnButton.returnToNormalForm'
                ]
              }{' '}
            </button>
          </div>
        </Col>
      )}
      <Col span={24}>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (disablePaymentSubmit || isSubmittingRef.current) return;
            isSubmittingRef.current = true;
            setIsSubmitting(true);
            try {
              await handleSubmit();
            } finally {
              isSubmittingRef.current = false;
              setIsSubmitting(false);
            }
          }}>
          <Row gutter={[0, 0]} className='payment-checkout-form'>
            <Col span={24}>
              <div className='payment-form-container'>
                <div className='payment-form-heading-container common-grid'>
                  <div className='payment-form-heading'>
                    {isCardPaymentMethod && (
                      <>
                        <label>
                          {
                            ApplicationString[
                              isApplePaySelected
                                ? 'specialPaymentFormComponent.payWithApplePay'
                                : 'specialPaymentFormComponent.payWithCreditCard'
                            ]
                          }
                        </label>
                        {showProcessingFeeNotice && (
                          <p className='payment-form-credit-card-notice'>
                            {
                              ApplicationString[
                                isApplePaySelected
                                  ? 'specialPaymentFormComponent.applePayProcessingFeeNotice'
                                  : 'specialPaymentFormComponent.creditCardProcessingFeeNotice'
                              ]
                            }
                          </p>
                        )}
                      </>
                    )}
                    {onProcessingFeeToggle && isCardPaymentMethod && (
                      <div className='checkout-processing-fee-toggle'>
                        <Checkbox
                          checked={isProcessingFeeAdded}
                          onChange={(e) =>
                            onProcessingFeeToggle(e.target.checked)
                          }>
                          <span className='checkout-processing-fee-toggle-label'>
                            {processingFeeBeforeAmountParts[0]}
                            <strong className='processing-fee-before-amount'>
                              (2.9%)
                            </strong>
                            {processingFeeBeforeAmountParts[1] || ''}
                            {formattedProcessingFee ? (
                              <strong className='processing-fee-amount'>
                                {' '}
                                ({formattedProcessingFee})
                              </strong>
                            ) : null}
                            {
                              ApplicationString[
                                'userAttendeeCartComponent.credit.coverProcessingFeeAfterAmount'
                              ]
                            }
                          </span>
                        </Checkbox>
                      </div>
                    )}
                  </div>
                  {/* <div className='stripe-logo'>
                    <img src={stripeLogo} alt='stripe' />
                  </div> */}
                </div>
                <div className='stripe-payment-element-wrap'>
                  <PaymentElement
                    options={{layout: 'tabs'}}
                    onReady={() => {
                      setPaymentElementError(null);
                      setIsPaymentElementReady(true);
                    }}
                    onLoadError={(event) => {
                      setPaymentElementError(
                        event?.error?.message ||
                          'Unable to load card payment fields. Refresh and try again.',
                      );
                    }}
                    onChange={(event) => {
                      const selectedType = event?.value?.type || null;
                      onPaymentMethodChange &&
                        onPaymentMethodChange(selectedType);
                    }}
                  />
                  {paymentElementError ? (
                    <p className='stripe-payment-element-error'>
                      {paymentElementError}
                    </p>
                  ) : null}
                </div>
              </div>
            </Col>

            <Col span={24}>
              <div className={'specialPaymentConfimAndPayContainer'}>
                <div>
                  {extra && (
                    <div className='attendees-description-container'>
                      <Alert
                        prefixCls='attendees-description-alert'
                        message={
                          <div dangerouslySetInnerHTML={{__html: extra}} />
                        }
                        type='info'
                      />
                    </div>
                  )}
                </div>
                <div className='confirmation-action-payment-button-container'>
                  <span>
                    {localChapterId
                      ? formatCurrencyByLocalChapter(
                          isProcessingFeeAdded
                            ? Math.round(Number(price || 0) * 1.029 * 100) / 100
                            : price,
                          eventLocalChapterData,
                          2,
                          2,
                        )
                      : formatCurrencyByLocalChapter(
                          isProcessingFeeAdded
                            ? Math.round(Number(price || 0) * 1.029 * 100) / 100
                            : price,
                          undefined,
                          2,
                          2,
                        )}
                  </span>
                  <button
                    className='confirmation-action-button confirmation-action-button-specialPayment'
                    type='submit'
                    disabled={!stripe || disablePaymentSubmit || isSubmitting}>
                    {localChapterId
                      ? currentLanguage === 'ja'
                        ? ApplicationString[
                            'specialPaymentFormComponent.confirmAndPay.japanese'
                          ]
                        : ApplicationString[
                            'specialPaymentFormComponent.confirmAndPay.english'
                          ]
                      : ApplicationString[
                          'specialPaymentFormComponent.confirmAndPay.english'
                        ]}
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};
//CommonPaymentCheckoutFormContainer

export default CommonPaymentCheckoutForm;
